import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from './AuthProvider';
import ServiceUnavailable from '../system/ServiceUnavailable';
import LoginPage from './LoginPage';

// Styled loading container
const LoadingContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f8fafc',
  gap: theme.spacing(2),
}));

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, isLoading, user, connectivity, retryConnectivityCheck } = useAuth();

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress 
          size={48} 
          sx={{ 
            color: '#1e40af',
            marginBottom: 2,
          }} 
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#64748b',
            fontWeight: 500,
          }}
        >
          Loading...
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#9ca3af',
          }}
        >
          Authenticating your session
        </Typography>
      </LoadingContainer>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Check role-based access if required roles are specified
  if (requiredRoles.length > 0) {
    const hasRequiredRole = user ? requiredRoles.some(role => user.roles.includes(role)) : false;
    if (!hasRequiredRole) {
      // If we cannot verify roles because server is unreachable, show Service Unavailable instead of denying
      if (connectivity === 'degraded') {
        return (
          <ServiceUnavailable 
            title="Permissions Unavailable"
            description="We can't verify your permissions while the server is unreachable. Please try again in a moment."
            onRetry={retryConnectivityCheck}
          />
        );
      }
      // Online but missing role -> Access Denied
      return (
        <LoadingContainer>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#ef4444',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Access Denied
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#64748b',
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            You don't have the required permissions to access this page. 
            Please contact your administrator if you believe this is an error.
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#9ca3af',
              marginTop: 2,
            }}
          >
            Required roles: {requiredRoles.join(', ')}
          </Typography>
        </LoadingContainer>
      );
    }
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute;
